"use client";

import React from "react";
import { SignUpButton } from "@clerk/nextjs";
import { usePathname } from "next/navigation";
import { onboardingUrlWithReturn } from "modules/onboarding/onboardingReturnPath";
import { cn } from "utils/utils";

interface SignUpButtonCTAProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "hero" | "navbar" | "primary" | "secondary" | "table";
  children?: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  /**
   * Where to land after auth; card CTAs pass the same page with the clicked
   * card's filter applied. Sign-up detours through onboarding carrying this as
   * `?from=`, sign-in goes straight there. When omitted, sign-up returns to the
   * current pathname and sign-in keeps Clerk's default of staying put, which
   * preserves the visitor's live query string.
   */
  returnUrl?: string;
}

const buttonSizeClasses = {
  sm: "btn-sm",
  md: "btn-md",
  lg: "btn-lg",
};

export function SignUpButtonCTA({
  className = "",
  size = "md",
  variant = "hero",
  children,
  icon,
  onClick,
  returnUrl,
}: SignUpButtonCTAProps) {
  // usePathname rather than useSearchParams on purpose: this renders in every
  // grid cell, and only the registering section may subscribe to search params.
  const pathname = usePathname();
  const signUpDestination = returnUrl ?? pathname;

  // Default text based on variant
  const getDefaultButtonText = () => {
    switch (variant) {
      case "hero":
        return "Start Trading";
      case "table":
        return "";
      case "navbar":
        return "Sign Up";
      case "primary":
        return "Create Account";
      case "secondary":
        return "Join Now";
      default:
        return "Sign Up";
    }
  };

  return (
    <SignUpButton
      mode="modal"
      forceRedirectUrl={onboardingUrlWithReturn(signUpDestination)}
      signInForceRedirectUrl={returnUrl}
    >
      {/* Icon-only variant needs a label; when visible text exists it IS the
          accessible name (a mismatched static aria-label fails WCAG 2.5.3) */}
      <button
        aria-label={variant === "table" && !children ? "Sign up" : undefined}
        className={cn(
          "btn btn-primary",
          variant === "hero" &&
            "shadow-[0_0_24px_oklch(var(--color-primary)/0.4)]",
          buttonSizeClasses[size],
          className
        )}
        onClick={() => onClick && onClick()}
      >
        {icon && icon}
        {children || getDefaultButtonText()}
      </button>
    </SignUpButton>
  );
}
